import { Mail, MapPin, Phone, ShieldCheck } from 'lucide-react'
import { useEffect } from 'react'
import SiteFooter from './SiteFooter'
import SiteHeader from './SiteHeader'

const phoneDisplay = '214-754-5500'
const phoneHref = 'tel:+12147545500'

function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'Privacy Policy | Robert W. Buchholz, P.C.'
  }, [])

  return (
    <>
      <a className="skip-link" href="#privacy-content">Skip to privacy policy</a>

      <SiteHeader />

      <main className="privacy-main" id="privacy-content">
        <section className="privacy-hero">
          <div className="container privacy-hero-inner">
            <p className="eyebrow">Your information</p>
            <h1>Privacy Policy</h1>
            <p>
              This policy explains how The Law Office of Robert W. Buchholz, P.C.
              collects, uses, and protects information submitted through this website.
            </p>
            <span>Effective July 26, 2026</span>
          </div>
        </section>

        <section className="privacy-content section">
          <div className="container privacy-layout">
            <aside className="privacy-summary">
              <ShieldCheck aria-hidden="true" />
              <h2>Privacy at a glance</h2>
              <p>
                The firm does not sell personal information. This website does not use
                advertising or analytics cookies. Contact-form information is used to
                review and respond to inquiries.
              </p>
            </aside>

            <article className="policy-copy">
              <section>
                <h2>Information collected</h2>
                <p>
                  If you use the contact form, the firm collects the information you
                  choose to provide, including your name, email address, phone number,
                  type of legal matter, and message. The website and its service
                  providers may also process limited technical information, such as an
                  IP address, browser or device information, request time, verification
                  results, and security logs, to operate and protect the website.
                </p>
              </section>

              <section>
                <h2>How information is used</h2>
                <p>The firm may use submitted information to:</p>
                <ul>
                  <li>Review and respond to an inquiry;</li>
                  <li>Determine whether the firm may be able to assist;</li>
                  <li>Operate, secure, and troubleshoot the website; and</li>
                  <li>Comply with applicable legal and professional obligations.</li>
                </ul>
                <p>
                  Submitting information does not create an attorney-client relationship.
                  Do not submit privileged, confidential, medical, or time-sensitive
                  information through the website.
                </p>
              </section>

              <section>
                <h2>Service providers and disclosure</h2>
                <p>
                  The website is hosted by DigitalOcean and uses Cloudflare Turnstile to
                  distinguish human visitors from automated submissions. Contact-form
                  submissions are transmitted through Resend and delivered by email to the
                  firm. These providers process information to provide hosting, security,
                  spam prevention, and email-delivery services. Information may also be
                  disclosed when required by law, to protect legal rights or website
                  security, or with your direction or consent.
                </p>
                <p>The firm does not sell or rent personal information.</p>
              </section>

              <section>
                <h2>Retention and security</h2>
                <p>
                  Contact information is retained only as reasonably necessary to review
                  and respond to an inquiry, maintain appropriate business records, and
                  satisfy legal or professional obligations. The firm uses reasonable
                  administrative and technical safeguards, including encrypted HTTPS
                  transmission. No internet or email transmission can be guaranteed to be
                  completely secure.
                </p>
              </section>

              <section>
                <h2>Cookies and external links</h2>
                <p>
                  The website does not use advertising or analytics cookies. DigitalOcean,
                  Cloudflare, and other security providers may use essential technologies
                  needed to deliver and protect the website and verify legitimate form
                  submissions. Links to third-party websites are governed by those third
                  parties’ own privacy practices.
                </p>
              </section>

              <section>
                <h2>Your choices</h2>
                <p>
                  You may choose not to use the contact form and call the office instead.
                  To ask about information previously submitted through this website,
                  contact the firm using the information below. The firm may need to retain
                  certain records when required by law or professional obligations.
                </p>
              </section>

              <section>
                <h2>Children and policy changes</h2>
                <p>
                  This website is not directed to children under 13, and the firm does not
                  knowingly collect personal information from children through the site.
                  This policy may be updated as the website or its practices change. The
                  effective date above identifies the current version.
                </p>
              </section>

              <section>
                <h2>Contact the firm</h2>
                <div className="policy-contact-list">
                  <a href={phoneHref}><Phone aria-hidden="true" />{phoneDisplay}</a>
                  <span><MapPin aria-hidden="true" />5220 Spring Valley Rd, Suite 618, Dallas, TX 75254</span>
                  <a href="/#contact"><Mail aria-hidden="true" />Contact form</a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <SiteFooter />
    </>
  )
}

export default PrivacyPolicy
